// src/components/signup/review.tsx
import { InfoIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { setApplicationStep } from '../../pages/signup';
import { Application } from '../../types/dbTypes';
import useApi from '../../utils/AxiosWrapper';
import { BusinessInfoForm } from './customers/BusinessInfo';
import { PersonalFormData } from './customers/AccountInfo';
import SignupFooter from './SignupFooter';
import TierReviewCard from './customers/TierReviewCard';
import MailingAddressCard, {
  MailingAddressHandle,
} from './customers/MailingAddressCard';
import {
  ApplicationStatus,
  ApplicationStep,
  Entity,
} from '../../types/apiTypes';
import { asTierType } from '../../utils/tierSelection';
import { useScreenView } from '../../utils/ScreenViewContext';
import { useUser } from '../../utils/UserContext';
import { formatNumberCommas } from '../../utils/CurrencyUtils';
import { formatUSPhonePretty } from '../../utils/phone';
import TrustSignals from './TrustSignals';
import useFrontChat from '../../hooks/useFrontChat';
import { safeSessionGetItem } from '../../shared/safeStorage';

interface ReviewProps {
  setStep: (step: string) => void;
  currStep: string;
  selectedRole: Entity;
  application: Application;
  initialPersonalInfo?: PersonalFormData | null;
  initialBusinessInfo?: BusinessInfoForm | null;
}

function Review({
  setStep,
  currStep,
  selectedRole,
  application,
  initialPersonalInfo,
  initialBusinessInfo,
}: ReviewProps) {
  useFrontChat();

  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [hasConsented, setHasConsented] = useState(false);
  const [loading, setLoading] = useState(false);
  const [personalInfo, setPersonalInfo] = useState<
    PersonalFormData | undefined
  >(undefined);
  const [businessInfo, setBusinessInfo] = useState<
    BusinessInfoForm | undefined
  >(undefined);

  const [showTooltip, setShowTooltip] = useState(false);
  // THT-819: the mailing-address card owns the choice; Continue asks it to apply that choice
  // (a failure keeps the customer here) before the existing submit runs.
  const mailingAddressRef = useRef<MailingAddressHandle>(null);
  // Continue only shows a spinner while it runs; a second click must not start a second save.
  const submittingRef = useRef(false);
  const [mailingAddressReady, setMailingAddressReady] = useState(true);
  const handleMailingAddressReady = useCallback(
    (ready: boolean) => setMailingAddressReady(ready),
    []
  );
  const { userId, userRecord } = useUser();

  /**
   * The carrier number to show back to the customer, and which column it sits in.
   *
   * A source is chosen first, and only then a column — never interleaved field by field. Taking
   * the MC from one source and the DOT from another lets a stale identifier outrank the answer
   * the customer just gave: the heads-up page (THT-729) saves an MC, the backend rejects it, the
   * customer bounces back and switches the toggle to DOT, and the submitted
   * `motor_carrier_number: undefined` is dropped by `JSON.stringify`, so the rejected MC survives
   * on the record. Preferring MC across both sources would then show them the very number they
   * were sent back to correct.
   *
   * Within a source, an explicit `carrier_type` picks the column; otherwise whichever column
   * holds a number wins. The record carries no type of its own, so it falls back to that order.
   * Null when neither source holds a number — the row is dropped rather than rendered blank.
   */
  const customerRecord =
    userRecord?.userType === Entity.Customer ? userRecord : undefined;

  const pickCarrierNumber = (
    mcNumber: string | undefined,
    dotNumber: string | undefined,
    preferred?: 'MC' | 'DOT'
  ) => {
    if (preferred === 'DOT' && dotNumber)
      return { label: 'DOT Number', value: dotNumber };
    if (preferred === 'MC' && mcNumber)
      return { label: 'MC Number', value: mcNumber };
    if (mcNumber) return { label: 'MC Number', value: mcNumber };
    if (dotNumber) return { label: 'DOT Number', value: dotNumber };
    return null;
  };

  const carrierNumberOnFile =
    pickCarrierNumber(
      businessInfo?.mc_number,
      businessInfo?.dot_number,
      businessInfo?.carrier_type
    ) ??
    pickCarrierNumber(
      customerRecord?.motor_carrier_number,
      customerRecord?.dot_number
    );

  /**
   * The tier to read back, sourced from the application first.
   *
   * Choose Tier writes the pick to the application's `selected_tier`; the customer record's
   * `current_tier` is only whatever the account was created with — the landing-page
   * `tach_selected_tier` cookie (see RoleSelect) or the backend default. Reading the record
   * first showed everyone that opening tier no matter what they picked a step earlier. The
   * record stays as the fallback for an application written before this step existed.
   *
   * `selected_tier` is typed as a bare string on the application, so an unrecognised value is
   * treated as no pick at all rather than indexed blindly into the tier config.
   */
  const chosenTier =
    asTierType(application?.selected_tier) ??
    customerRecord?.current_tier ??
    null;

  const handleSetLoading = (value: boolean) => {
    setLoading(value);
  };

  useEffect(() => {
    if (initialPersonalInfo?.firstName?.length) {
      setPersonalInfo(initialPersonalInfo);
    }
    if (initialBusinessInfo?.businessName?.length) {
      setBusinessInfo(initialBusinessInfo);
    }
  }, [initialBusinessInfo, initialPersonalInfo]);

  useEffect(() => {
    if (personalInfo && businessInfo) return;
    const savedDataPersonal = safeSessionGetItem('personalInfo');
    if (savedDataPersonal) {
      const parsedData: PersonalFormData = JSON.parse(savedDataPersonal);
      setPersonalInfo(parsedData);
    }
    const savedDataBusiness = safeSessionGetItem('businessInfo');
    if (savedDataBusiness) {
      const parsedData: BusinessInfoForm = JSON.parse(savedDataBusiness);
      setBusinessInfo(parsedData);
    }
  }, [businessInfo, personalInfo]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full w-full flex-col">
            {!loading ? (
              <>
                <div className="w-full flex-1 overflow-y-auto py-10 lg:overflow-visible lg:py-20">
                  <div className="flex w-full flex-col justify-center px-6 lg:flex-row lg:px-12">
                    <div className="flex flex-col items-center justify-center gap-8">
                      <div className="mx-auto flex-1 lg:w-[1050px] lg:max-w-[1050px]">
                        <div className="mx-auto mb-6 max-w-[1000px] text-center lg:mb-8 lg:max-w-full lg:text-start">
                          <h2 className="text-[30px] font-bold">
                            Review Your Information
                          </h2>
                        </div>
                        {/* Where cards are mailed if approved (THT-819) — applied on Continue. */}
                        <div className="mx-auto mb-4 w-full max-w-[1000px] lg:mx-0">
                          <MailingAddressCard
                            customerId={userId ?? ''}
                            businessInfo={businessInfo}
                            personalInfo={personalInfo}
                            ref={mailingAddressRef}
                            onReadyChange={handleMailingAddressReady}
                          />
                        </div>
                        <div className="mx-auto grid w-full max-w-[1000px] grid-cols-1 justify-center gap-6 lg:mx-0 lg:grid-cols-2 lg:justify-between">
                          {/* Owner Information Table */}
                          <div className="rounded-[4px] border-[1px] border-bordergray p-3">
                            <div className="mb-4 flex w-full flex-row items-center justify-between align-middle">
                              <h2 className="text-lg font-semibold">
                                Owner Information
                              </h2>
                              <button
                                onClick={() => setStep('personal_info')}
                                className="flex flex-row items-center space-x-1 align-middle text-[16px] font-[700] text-accent underline"
                              >
                                Edit <PencilSimpleIcon />
                              </button>
                            </div>
                            <div className="grid grid-cols-2">
                              <div className="bg-primarybg p-1 px-3 text-gray-600">
                                First Name
                              </div>
                              <div className="bg-primarybg p-1 px-3 font-medium">
                                {personalInfo?.firstName}
                              </div>

                              <div className="p-1 px-3 text-gray-600">
                                Last Name
                              </div>
                              <div className="p-1 px-3 font-medium">
                                {personalInfo?.lastName}
                              </div>

                              <div className="bg-primarybg p-1 px-3 text-gray-600">
                                Email
                              </div>
                              <div className="whitespace-normal break-words bg-primarybg p-1 px-3 font-medium">
                                {personalInfo?.email}
                              </div>

                              <div className="p-1 px-3 text-gray-600">
                                Phone Number
                              </div>
                              <div className="p-1 px-3 font-medium">
                                {formatUSPhonePretty(
                                  personalInfo?.phoneNumber ?? ''
                                )}
                              </div>

                              <div className="bg-primarybg p-1 px-3 text-gray-600">
                                Date of Birth
                              </div>
                              <div className="bg-primarybg p-1 px-3 font-medium">
                                {personalInfo?.dateOfBirth}
                              </div>

                              <div className="p-1 px-3 text-gray-600">
                                Address Line 1
                              </div>
                              <div className="p-1 px-3 font-medium">
                                {personalInfo?.address_st_number +
                                  ' ' +
                                  personalInfo?.address_st_name}
                              </div>

                              <div className="bg-primarybg p-1 px-3 text-gray-600">
                                Address Line 2
                              </div>
                              <div className="bg-primarybg p-1 px-3 font-medium">
                                {personalInfo?.address_unit}
                              </div>

                              <div className="p-1 px-3 text-gray-600">City</div>
                              <div className="p-1 px-3 font-medium">
                                {personalInfo?.address_city}
                              </div>

                              <div className="bg-primarybg p-1 px-3 text-gray-600">
                                State
                              </div>
                              <div className="bg-primarybg p-1 px-3 font-medium">
                                {personalInfo?.address_state}
                              </div>

                              <div className="p-1 px-3 text-gray-600">ZIP</div>
                              <div className="p-1 px-3 font-medium">
                                {personalInfo?.address_zip}
                              </div>

                              <div className="bg-primarybg p-1 px-3 text-gray-600">
                                SSN
                              </div>
                              <div className="bg-primarybg p-1 px-3 font-medium">
                                ***-**-
                                {personalInfo?.ssn.slice(
                                  personalInfo?.ssn?.length - 4,
                                  personalInfo?.ssn?.length
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Business Information Table */}
                          <div className="rounded-[4px] border-[1px] border-bordergray p-3">
                            <div className="mb-4 flex w-full flex-row items-center justify-between align-middle">
                              <h2 className="text-lg font-semibold">
                                Business Information
                              </h2>
                              <button
                                onClick={() => setStep('business_info')}
                                className="flex flex-row items-center space-x-1 align-middle text-[16px] font-[700] text-accent underline"
                              >
                                Edit <PencilSimpleIcon />
                              </button>
                            </div>
                            <div className="row- grid grid-cols-2">
                              <div className="p-1 px-3 text-gray-600">
                                Legal Business Name
                              </div>
                              <div className="p-1 px-3 font-medium">
                                {businessInfo?.businessName}
                              </div>
                              <div className="bg-primarybg p-1 px-3 text-gray-600">
                                Business Phone Number
                              </div>
                              <div className="bg-primarybg p-1 px-3 font-medium">
                                {formatUSPhonePretty(
                                  businessInfo?.businessPhone ?? ''
                                )}
                              </div>

                              <div className="p-1 px-3 text-gray-600">
                                Address Line 1
                              </div>
                              <div className="p-1 px-3 font-medium">
                                {businessInfo?.address_st_number +
                                  ' ' +
                                  businessInfo?.address_st_name}
                              </div>

                              <div className="bg-primarybg p-1 px-3 text-gray-600">
                                Address Line 2
                              </div>
                              <div className="bg-primarybg p-1 px-3 font-medium">
                                {businessInfo?.address_unit}
                              </div>

                              <div className="p-1 px-3 text-gray-600">City</div>
                              <div className="p-1 px-3 font-medium">
                                {businessInfo?.address_city}
                              </div>

                              <div className="bg-primarybg p-1 px-3 text-gray-600">
                                State
                              </div>
                              <div className="bg-primarybg p-1 px-3 font-medium">
                                {businessInfo?.address_state}
                              </div>

                              <div className="p-1 px-3 text-gray-600">ZIP</div>
                              <div className="p-1 px-3 font-medium">
                                {businessInfo?.address_zip}
                              </div>

                              <div className="bg-primarybg p-1 px-3 text-gray-600">
                                EIN
                              </div>
                              <div className="bg-primarybg p-1 px-3 font-medium">
                                {businessInfo?.taxId}
                              </div>

                              <div className="p-1 px-3 text-gray-600">
                                Average Annual Revenue
                              </div>
                              <div className="p-1 px-3 font-medium">
                                $
                                {formatNumberCommas(
                                  businessInfo?.annualRevenue ?? 0
                                )}
                              </div>

                              <div className="bg-primarybg p-1 px-3 text-gray-600">
                                Years in Business
                              </div>
                              <div className="bg-primarybg p-1 px-3 font-medium">
                                {businessInfo?.yearsInBusiness}
                              </div>
                              {/* THT-729: Business Info no longer asks for the MC/DOT number
                                  once the heads-up page has it, so session storage may hold
                                  nothing — fall back to the saved customer record, which is
                                  where that answer now lives. */}
                              {carrierNumberOnFile ? (
                                <>
                                  <div className="p-1 px-3 text-gray-600">
                                    {carrierNumberOnFile.label}
                                  </div>
                                  <div className="p-1 px-3 font-medium">
                                    {carrierNumberOnFile.value}
                                  </div>
                                </>
                              ) : null}

                              {businessInfo?.referralCode?.length ? (
                                <>
                                  <div className="bg-primarybg p-1 px-3 font-medium">
                                    Referral Code
                                  </div>
                                  <div className="bg-primarybg p-1 px-3 font-medium">
                                    {businessInfo?.referralCode}
                                  </div>
                                </>
                              ) : null}

                              <div className="p-1 px-3 text-gray-600">
                                Factoring Company Name
                              </div>
                              <div className="p-1 px-3 font-medium">
                                {(userRecord as any)?.factoring_company_name ||
                                  'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Membership Tier Card — a read-back of the Choose Tier pick; Edit
                            is local-only navigation, never a resubmit (THT-773). */}
                        <div className="mx-auto mt-6 w-full max-w-[1000px] lg:mx-0">
                          <TierReviewCard
                            tier={chosenTier}
                            onEdit={() => setStep(ApplicationStep.ChoosePlan)}
                          />
                        </div>
                        <div className="mx-auto flex flex-row items-center justify-center space-x-1 py-3 align-middle text-xs text-[#7D8191] md:text-[16px] lg:mx-0 lg:justify-start">
                          <InfoIcon size={14} className="md:h-4 md:w-4" />
                          <p className="md:hidden">
                            Please ensure your information is correct.
                          </p>
                          <p className="hidden md:block">
                            Please ensure your information is correct before
                            proceeding.
                          </p>
                        </div>
                        <div className="mx-auto mb-8 flex w-full max-w-[95%] flex-col items-start justify-center lg:mx-0 lg:max-w-[80%]">
                          <div className="flex items-center py-8">
                            <div className="flex h-5 items-center">
                              <input
                                id="owns25"
                                type="checkbox"
                                checked={hasConsented}
                                onChange={() => setHasConsented(!hasConsented)}
                                className="h-8 w-8 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </div>
                            <label
                              htmlFor="owns25"
                              className="ml-3 text-[16px] leading-6 text-gray-700"
                            >
                              I confirm that I own{' '}
                              <b className="font-bold underline">
                                at least 25%
                              </b>{' '}
                              of my trucking company.
                            </label>
                          </div>
                        </div>
                        <SignupFooter
                          disableSubmit={!hasConsented || !mailingAddressReady}
                          selectedRole={selectedRole}
                          currStep={currStep}
                          showTooltip={showTooltip}
                          onMouseEnter={() => setShowTooltip(true)}
                          onMouseLeave={() => setShowTooltip(false)}
                          isMobile={screenView === 'mobile'}
                          onBackClick={async () => {
                            // Choose Tier now sits between Owner Info and Review (THT-773) —
                            // Review's back goes there, not all the way to Owner Info.
                            const currStep = await setApplicationStep({
                              step: ApplicationStep.ChoosePlan,
                              application_id: application?.application_id,
                              customer_id: userId ?? '',
                              callProtectedApi,
                              setLoading: handleSetLoading,
                              previousStep: ApplicationStep.Review,
                              applicationStatus:
                                application?.status as ApplicationStatus,
                            });
                            if (currStep === ApplicationStep.ChoosePlan) {
                              setStep(ApplicationStep.ChoosePlan);
                            }
                          }}
                          onSubmitClick={async () => {
                            if (submittingRef.current) return;
                            submittingRef.current = true;
                            try {
                              const mailingAddressSaved =
                                (await mailingAddressRef.current?.apply()) ??
                                true;
                              if (!mailingAddressSaved) return;
                              // No selected_tier here — the choice was already made (and sent) on
                              // Choose Tier; Review only confirms it (THT-773).
                              const currStep = await setApplicationStep({
                                step: ApplicationStep.MasterServiceAgreement,
                                application_id: application?.application_id,
                                customer_id: userId ?? '',
                                callProtectedApi,
                                setLoading: handleSetLoading,
                                previousStep: ApplicationStep.Review,
                                applicationStatus:
                                  application?.status as ApplicationStatus,
                              });
                              if (
                                currStep ===
                                ApplicationStep.MasterServiceAgreement
                              ) {
                                setStep(ApplicationStep.MasterServiceAgreement);
                              }
                            } finally {
                              submittingRef.current = false;
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="mx-auto pt-6 lg:mx-0">
                      {/* Desktop sidebar with trust signals */}
                      {screenView === 'desktop' ? (
                        <div className="sticky top-8 hidden h-fit w-[350px] lg:block">
                          <TrustSignals variant="sidebar" />
                        </div>
                      ) : (
                        <div className="mb-4 w-full">
                          <TrustSignals variant="mobile" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="h-full w-full justify-center"
                />
              </div>
            )}
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default Review;
